import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  BasemapId,
  MML_ATTRIBUTION,
  MML_BASEMAPS,
  MML_LAYER_IDS,
  MML_TILE,
} from "../../utils/mmlBasemaps";
import { WGS84lalToETRSTM35FINxy } from "../../utils/coordinateConverter";
import { geometryToWsg84 } from "../../utils/road";
import {
  findNearestRoadResult,
  getRoadGeometryByLinkId,
  getRoadInfo,
  roadLookupError,
} from "../../services/vayvaService";
import useMapDrawing from "../../pages/pages/useMapDrawing";
import { SUPPORT_PATH } from "../../utils/support";
import { geocodePlace } from "../../services/maanmittauslaitosService";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || "";

/** Whole-Finland view, used when the typed municipality does not geocode. */
const FINLAND_CENTER: [number, number] = [25.7, 64.5];
const FINLAND_ZOOM = 4;

/**
 * Väylä snaps a click to the nearest road in the national register, and its
 * search radius is generous — a click can resolve to a road 50 m away. Past
 * this distance we still show the hit but warn that it may not be the one
 * the user meant.
 */
const FAR_CLICK_WARNING_M = 30;

const HIGHLIGHT_SOURCE = "picked-road";
const NUMBER_LAYER = `${HIGHLIGHT_SOURCE}-numbers`;

/**
 * Puts the given GeoJSON on the highlight source, creating the source and its
 * two layers on first use. The number layer labels each part so the map can be
 * read against the list of selected stretches.
 */
const setHighlight = (map: mapboxgl.Map, data: any) => {
  const source = map.getSource(HIGHLIGHT_SOURCE) as
    | mapboxgl.GeoJSONSource
    | undefined;

  if (source) {
    source.setData(data);
    return;
  }

  map.addSource(HIGHLIGHT_SOURCE, { type: "geojson", data });
  map.addLayer({
    id: HIGHLIGHT_SOURCE,
    type: "line",
    source: HIGHLIGHT_SOURCE,
    layout: { "line-join": "round", "line-cap": "round" },
    paint: { "line-color": "#ffa500", "line-width": 4 },
  });
  map.addLayer({
    id: NUMBER_LAYER,
    type: "symbol",
    source: HIGHLIGHT_SOURCE,
    layout: {
      "symbol-placement": "line-center",
      "text-field": ["get", "label"],
      "text-size": 16,
      // Short stretches lose their number to collision otherwise, which
      // defeats the point of numbering them.
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": "#7a3e00",
      "text-halo-color": "#ffffff",
      "text-halo-width": 2,
    },
  });
};

/** Metres along the given WGS84 line parts. */
const lengthOfParts = (parts: number[][][]): number =>
  parts.reduce(
    (total, part) =>
      total + turf.length(turf.lineString(part), { units: "meters" }),
    0
  );

export type RoadSource = "VAYLA" | "LINK_ID" | "MANUAL";

export interface PickedRoad {
  name: string;
  municipality: string;
  lengthMeters: number | null;
  source: RoadSource;
  /**
   * Line parts in WGS84, sent to registration when the register could not
   * resolve the road by name. Absent for a normal VAYLA pick, where the
   * backend looks the geometry up itself.
   */
  parts?: number[][][];
  /** The register has no name for this road, so the user must supply one. */
  needsName?: boolean;
}

/** One selected stretch of road, kept locally so a repeat click can toggle it. */
export interface RoadPart {
  linkId: string;
  coordinates: number[][];
  lengthMeters: number;
}

export interface PickFailure {
  kind: "no-road" | "no-geometry";
  name?: string;
  municipality?: string;
  apiError?: string;
  lngLat: [number, number];
}

interface SignUpRoadMapProps {
  /** What the user typed on step 2 — used only to centre the initial view. */
  municipality: string;
  picked: PickedRoad | null;
  onRoadPicked: (road: PickedRoad | null) => void;
  height?: string;
}

const BASEMAP_LABELS: { id: BasemapId; label: string }[] = [
  { id: "mml-maastokartta", label: "Maastokartta" },
  { id: "mml-taustakartta", label: "Taustakartta" },
  { id: "mml-ortokuva", label: "Ilmakuva" },
];

/**
 * Lets a registering user point at their road on a map when the name lookup
 * could not find it.
 *
 * A click anywhere is enough — we do not require the user to hit a rendered
 * road feature, because the Mapbox street tiles do not reliably carry small
 * private roads. Väylä's own snap does, and it is the same register that the
 * name lookup queries, so whatever it returns is guaranteed to be findable
 * again by name.
 */
const SignUpRoadMap: React.FC<SignUpRoadMapProps> = ({
  municipality,
  picked,
  onRoadPicked,
  height = "480px",
}) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>("mml-taustakartta");

  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<PickFailure | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  // Stateful copy of the map so the drawing hook re-derives once it has loaded;
  // the layer effects keep using mapRef. Same shape as PropertyMiniMap.
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);

  /**
   * The selected stretches. Owned here rather than by the parent: removal has
   * to redraw the map, and nothing syncs `picked` back into the highlight
   * source. Safe because Step3 unmounts this component when it leaves step 3.
   */
  const [parts, setParts] = useState<RoadPart[]>([]);
  const partsRef = useRef<RoadPart[]>([]);
  partsRef.current = parts;

  /** Parts displaced by picking a register-named road, for Kumoa. */
  const [replacedParts, setReplacedParts] = useState<RoadPart[] | null>(null);

  /**
   * Bumped whenever the selection is cleared or replaced. A response whose
   * generation is stale is dropped; within a generation, responses append in
   * whatever order they arrive, so two quick clicks both land.
   */
  const generation = useRef(0);

  const { initializeDrawing, drawInstance } = useMapDrawing(mapInstance);

  const warnIfFar = useCallback((distanceM: number) => {
    if (distanceM > FAR_CLICK_WARNING_M) {
      setWarning(
        `Lähin tie oli ${Math.round(distanceM)} metrin päässä ` +
          `klikkauksestasi. Varmista, että kyseessä on oikea tie.`
      );
    }
  }, []);

  /**
   * Renders every selected stretch as its own feature so each can carry its
   * number, letting the user match what is on the map to the list below it.
   * A single MultiLineString would draw the same lines but could not be
   * labelled per part.
   */
  const renderParts = useCallback((list: RoadPart[], fit: boolean) => {
    const map = mapRef.current;
    if (!map) return;

    const data: any = {
      type: "FeatureCollection",
      features: list.map((part, index) => ({
        type: "Feature",
        properties: { label: String(index + 1) },
        geometry: { type: "LineString", coordinates: part.coordinates },
      })),
    };

    setHighlight(map, data);

    if (!fit || !list.length) return;

    const bounds = turf.bbox(data);
    if (bounds.every((n) => Number.isFinite(n))) {
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 60, maxZoom: 15, duration: 800 }
      );
    }
  }, []);

  /** A register-named road arrives whole; it is drawn but never accumulated. */
  const drawWholeRoad = useCallback((geometry: any) => {
    const map = mapRef.current;
    if (!map) return;

    setHighlight(map, { type: "Feature", properties: {}, geometry });

    const bounds = turf.bbox(geometry);
    if (bounds.every((n) => Number.isFinite(n))) {
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 60, maxZoom: 15, duration: 800 }
      );
    }
  }, []);

  const clearRoad = useCallback(() => {
    const map = mapRef.current;
    const source = map?.getSource(HIGHLIGHT_SOURCE) as
      | mapboxgl.GeoJSONSource
      | undefined;
    source?.setData({ type: "FeatureCollection", features: [] } as any);
  }, []);

  /** Push the accumulated stretches up as one road. */
  const publishParts = useCallback(
    (list: RoadPart[], municipalityName: string) => {
      if (!list.length) {
        onRoadPicked(null);
        return;
      }
      onRoadPicked({
        name: "",
        municipality: municipalityName,
        lengthMeters: list.reduce((sum, part) => sum + part.lengthMeters, 0),
        source: "LINK_ID",
        parts: list.map((part) => part.coordinates),
        needsName: true,
      });
    },
    [onRoadPicked]
  );

  const reportFailure = useCallback(
    (failure: PickFailure) => {
      // A misfired click must not throw away stretches already collected —
      // assembling a road can take several clicks and one of them landing in a
      // field should cost nothing.
      if (!partsRef.current.length) {
        onRoadPicked(null);
        clearRoad();
      }
      setFailure(failure);
    },
    [clearRoad, onRoadPicked]
  );

  const setSelection = useCallback(
    (list: RoadPart[], fit = false) => {
      setParts(list);
      partsRef.current = list;
      renderParts(list, fit);
      publishParts(list, municipality);
    },
    [municipality, publishParts, renderParts]
  );

  const removePart = useCallback(
    (index: number) => {
      setWarning(null);
      setSelection(partsRef.current.filter((_, i) => i !== index));
    },
    [setSelection]
  );

  const clearSelection = useCallback(() => {
    generation.current += 1;
    setFailure(null);
    setWarning(null);
    setReplacedParts(null);
    setSelection([]);
  }, [setSelection]);

  /** Put back the stretches a register-named road displaced. */
  const undoReplace = useCallback(() => {
    if (!replacedParts) return;
    generation.current += 1;
    setReplacedParts(null);
    setSelection(replacedParts, true);
  }, [replacedParts, setSelection]);

  const handleClick = useCallback(
    async (event: mapboxgl.MapMouseEvent) => {
      if (drawing) return; // the draw tool owns clicks while it is active

      const myGeneration = generation.current;

      setLoading(true);
      setFailure(null);
      setWarning(null);

      const { lng, lat } = event.lngLat.wrap();
      const lngLat: [number, number] = [lng, lat];

      try {
        const { E, N } = WGS84lalToETRSTM35FINxy({ La: lat, Lo: lng });

        const result = await findNearestRoadResult(E, N);
        if (myGeneration !== generation.current) return;

        if (result.kind === "none") {
          reportFailure({ kind: "no-road", lngLat });
          return;
        }

        // The register knows a link here but has no name for it — typical of
        // private roads. Its geometry is reachable by link_id, so the stretch
        // is usable; only the name has to come from the user. A road often
        // spans several links, so clicks accumulate.
        if (result.kind === "unnamed") {
          const existing = partsRef.current;
          const alreadyAt = existing.findIndex(
            (part) => part.linkId === result.link.linkId
          );

          if (alreadyAt !== -1) {
            const remaining = existing.filter((_, i) => i !== alreadyAt);
            setParts(remaining);
            partsRef.current = remaining;
            renderParts(remaining, false);
            publishParts(remaining, result.link.municipality ?? municipality);
            return;
          }

          const link = await getRoadGeometryByLinkId(result.link.linkId);
          if (myGeneration !== generation.current) return;

          if (!link) {
            reportFailure({
              kind: "no-geometry",
              municipality: result.link.municipality ?? undefined,
              lngLat,
            });
            return;
          }

          const part: RoadPart = {
            linkId: result.link.linkId,
            coordinates: link.coordinates,
            lengthMeters:
              link.lengthMeters ??
              Math.round(lengthOfParts([link.coordinates])),
          };

          // Append to whatever is current rather than to the list captured when
          // this click started, so two quick clicks both survive.
          const next = [...partsRef.current, part];
          setParts(next);
          partsRef.current = next;
          // Reframe on every added stretch. The whole point of the list is that
          // the user can check what they have collected, and a stretch added
          // outside the current view would be invisible to them.
          renderParts(next, true);
          publishParts(next, result.link.municipality ?? municipality);
          warnIfFar(result.link.distanceM);
          return;
        }

        const nearest = result.road;

        // Resolve the full geometry by the canonical name — this is the same
        // call registration will make, so a hit here proves registration works.
        const response = await getRoadInfo(nearest.name, nearest.municipality);
        if (myGeneration !== generation.current) return;

        const feature = response.data?.features?.[0];
        const geometryError = roadLookupError(response.data);
        const hasLine =
          Array.isArray(feature?.geometry?.coordinates) &&
          feature.geometry.coordinates.length > 0;

        // Some roads are in the register by name but have no usable line —
        // most often "Kadun geometria ei ole yhtenäinen", a discontiguous
        // street the interval search cannot resolve. No parameter combination
        // rescues it, so this road cannot be registered by name at all.
        if (geometryError || !hasLine) {
          reportFailure({
            kind: "no-geometry",
            name: nearest.name,
            municipality: nearest.municipality,
            apiError: geometryError ?? undefined,
            lngLat,
          });
          return;
        }

        // A named road is a complete answer on its own — the backend re-looks
        // it up and stores no client geometry — so it cannot be one stretch
        // among many. Stash what it displaces so Kumoa can put it back.
        generation.current += 1;
        if (partsRef.current.length) setReplacedParts(partsRef.current);
        setParts([]);
        partsRef.current = [];

        drawWholeRoad({
          ...feature.geometry,
          coordinates: geometryToWsg84(feature),
        });

        const lengthMeters = Number(feature.properties?.viivan_pituus);
        onRoadPicked({
          name: nearest.name,
          municipality: nearest.municipality,
          lengthMeters: Number.isFinite(lengthMeters)
            ? Math.round(lengthMeters)
            : null,
          source: "VAYLA",
        });

        warnIfFar(nearest.distanceM);
      } catch {
        if (myGeneration !== generation.current) return;
        reportFailure({ kind: "no-road", lngLat });
      } finally {
        setLoading(false);
      }
    },
    [
      drawing,
      drawWholeRoad,
      municipality,
      onRoadPicked,
      publishParts,
      renderParts,
      reportFailure,
      warnIfFar,
    ]
  );

  // Keep the click handler fresh without tearing the map down on every render.
  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  // Create the map once per mount.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/outdoors-v11",
      center: FINLAND_CENTER,
      zoom: FINLAND_ZOOM,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      for (const layer of MML_LAYER_IDS) {
        map.addSource(layer, {
          type: "raster",
          tiles: [MML_TILE(MML_BASEMAPS[layer])],
          tileSize: 256,
          attribution: MML_ATTRIBUTION,
        });
        map.addLayer({
          id: layer,
          type: "raster",
          source: layer,
          layout: { visibility: "none" },
        });
      }
      setMapReady(true);
      setMapInstance(map);
    });

    map.on("click", (event) => handleClickRef.current(event));
    map.on("mouseenter", () => {
      map.getCanvas().style.cursor = "crosshair";
    });

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
      setMapInstance(null);
    };
  }, []);

  useEffect(() => {
    if (mapInstance) initializeDrawing();
  }, [mapInstance, initializeDrawing]);

  /**
   * A drawn road is the last resort — for these the register has nothing at
   * all, so the user traces the line and names it. MapboxDraw already stores
   * WGS84 lon/lat, so unlike the Väylä path there is no conversion here.
   */
  const finishDrawing = useCallback(() => {
    const parts = (drawInstance?.getAll()?.features ?? [])
      .filter(
        (f: any) =>
          f.geometry?.type === "LineString" &&
          Array.isArray(f.geometry.coordinates) &&
          f.geometry.coordinates.length >= 2
      )
      .map((f: any) => f.geometry.coordinates as number[][]);

    if (!parts.length) {
      setWarning(
        "Piirrä tie kartalle klikkaamalla sen varrelle vähintään kaksi pistettä."
      );
      return;
    }

    setDrawing(false);
    setFailure(null);
    generation.current += 1;
    drawInstance?.changeMode("simple_select");

    onRoadPicked({
      name: "",
      municipality,
      lengthMeters: Math.round(lengthOfParts(parts)),
      source: "MANUAL",
      parts,
      needsName: true,
    });
  }, [drawInstance, municipality, onRoadPicked]);

  const startDrawing = useCallback(() => {
    // Drawing and clicking are alternatives, not a mix: a road is either
    // assembled from register links or traced by hand, which is what keeps
    // geometrySource meaningful.
    clearSelection();
    clearRoad();
    setDrawing(true);
    drawInstance?.changeMode("draw_line_string");
  }, [clearRoad, clearSelection, drawInstance]);

  const cancelDrawing = useCallback(() => {
    setDrawing(false);
    setWarning(null);
    drawInstance?.deleteAll();
    drawInstance?.changeMode("simple_select");
  }, [drawInstance]);

  // Basemap layers are added hidden, so switching only flips visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;

    for (const id of MML_LAYER_IDS) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(
          id,
          "visibility",
          id === basemap ? "visible" : "none"
        );
      }
    }
  }, [mapReady, basemap]);

  // Centre on the municipality the user typed. It is usually right even when
  // the road name is not, and it saves them panning across the country.
  const centredRef = useRef(false);
  useEffect(() => {
    // Centre once. `municipality` is written back by the parent on every pick,
    // so without this guard each click would re-centre on the town and fight
    // the framing of the road being assembled.
    if (!mapReady || !municipality.trim() || centredRef.current) return;
    centredRef.current = true;

    let cancelled = false;
    geocodePlace(municipality).then((center) => {
      if (cancelled || !center || !mapRef.current) return;
      mapRef.current.jumpTo({ center, zoom: 11 });
    });

    return () => {
      cancelled = true;
    };
  }, [mapReady, municipality]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Klikkaa karttaa tiesi kohdalta. Jos tiesi koostuu useasta osasta,
        klikkaa jokainen niistä — osat yhdistetään yhdeksi tieksi. Klikkaamalla
        valittua osaa uudelleen poistat sen.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={basemap}
        onChange={(_, value) => value && setBasemap(value as BasemapId)}
        aria-label="Karttataso"
      >
        {BASEMAP_LABELS.map(({ id, label }) => (
          <ToggleButton key={id} value={id}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {drawing ? (
        <Alert
          severity="info"
          action={
            <>
              <Button color="inherit" size="small" onClick={finishDrawing}>
                Valmis
              </Button>
              <Button color="inherit" size="small" onClick={cancelDrawing}>
                Peruuta
              </Button>
            </>
          }
        >
          Klikkaa tien varrelle piste kerrallaan. Kaksoisklikkaus päättää
          viivan.
        </Alert>
      ) : (
        <Box>
          <Button size="small" onClick={startDrawing}>
            Tie puuttuu kartalta — piirrä se itse
          </Button>
        </Box>
      )}

      <Box sx={{ position: "relative", height, minHeight: 260 }}>
        <div
          ref={containerRef}
          className="map-container"
          style={{ position: "absolute", inset: 0 }}
        />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxShadow: 2,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption">Haetaan tietä…</Typography>
          </Box>
        )}
      </Box>

      {failure && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                // Hand the diagnostics to the support form rather than asking
                // the user to copy them across.
                navigate(SUPPORT_PATH, {
                  state: {
                    roadProblem: {
                      municipality: failure.municipality ?? municipality,
                      roadName: failure.name,
                      lngLat: failure.lngLat,
                      apiError: failure.apiError,
                    },
                  },
                })
              }
            >
              Ota yhteyttä
            </Button>
          }
        >
          {failure.kind === "no-road" ? (
            <>
              Tästä kohdasta ei löytynyt tietä. Kokeile klikata lähemmäs tien
              viivaa, piirrä tie itse, tai ota yhteyttä tukeen.
            </>
          ) : (
            <>
              {failure.name ? `Tietä "${failure.name}" ` : "Tätä tietä "}
              ei voi valita: sen reittitiedot puuttuvat tierekisteristä
              {failure.apiError ? ` (${failure.apiError})` : ""}. Piirrä tie
              itse tai ota yhteyttä tukeen.
            </>
          )}
        </Alert>
      )}

      {replacedParts && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={undoReplace}>
              Kumoa
            </Button>
          }
        >
          Valitsemasi tie löytyy tierekisteristä kokonaisena, joten aiemmin
          valitsemasi {replacedParts.length} osaa poistettiin.
        </Alert>
      )}

      {parts.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Valitut osat ({parts.length}) · yhteensä{" "}
            {parts.reduce((sum, part) => sum + part.lengthMeters, 0)} m
          </Typography>
          <List dense disablePadding>
            {parts.map((part, index) => (
              <ListItem
                key={part.linkId}
                disableGutters
                secondaryAction={
                  <Button size="small" onClick={() => removePart(index)}>
                    Poista
                  </Button>
                }
              >
                <ListItemText
                  primary={`${index + 1}. osa · ${part.lengthMeters} m`}
                />
              </ListItem>
            ))}
          </List>
          <Button size="small" onClick={clearSelection}>
            Tyhjennä kaikki
          </Button>
        </Box>
      )}

      {picked && (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                clearSelection();
                clearRoad();
              }}
            >
              Vaihda
            </Button>
          }
        >
          <strong>
            {[picked.name || "Nimetön tie", picked.municipality]
              .filter(Boolean)
              .join(", ")}
          </strong>
          {picked.lengthMeters !== null && ` · ${picked.lengthMeters} m`}
        </Alert>
      )}

      {warning && <Alert severity="info">{warning}</Alert>}
    </Stack>
  );
};

export default SignUpRoadMap;
